import { ArrowLeft, Ban, Mail, Phone, Save, ShieldAlert, ShieldCheck } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Avatar } from "../../components/Avatar";
import { CollectionPagination } from "../../components/CollectionPagination";
import { PageError, PageLoader } from "../../components/StateViews";
import { useSession } from "../../context/SessionContext";
import { useToast } from "../../context/ToastContext";
import { COLLECTION_PAGE_SIZE, usePagedCollection } from "../../hooks/usePagedCollection";
import { apiEnvelope, apiRequest, queryString } from "../../lib/api";
import { formatDate, formatRelative } from "../../lib/format";
import type { CustomerDetail, CustomerRecord } from "../../lib/types";

const EMPTY_FORM = { name: "", email: "", phone: "", locale: "", timezone: "" };

function profileForm(customer: CustomerRecord) {
  return {
    name: customer.name,
    email: customer.email ?? "",
    phone: customer.phone ?? "",
    locale: customer.locale ?? "en",
    timezone: customer.timezone ?? "UTC",
  };
}

export function AgentCustomerDetailPage() {
  const { id = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useSession();
  const { showToast } = useToast();
  const page = Math.max(1, Math.floor(Number(params.get("page")) || 1));
  const changePage = useCallback((nextPage: number) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      if (nextPage <= 1) next.delete("page"); else next.set("page", String(nextPage));
      return next;
    }, { replace: true });
  }, [setParams]);
  const routeIdRef = useRef(id);
  routeIdRef.current = id;
  const previousIdRef = useRef(id);
  const formOwnerRef = useRef("");
  const formDirtyRef = useRef(false);
  const [customerState, setCustomerState] = useState<{ ownerId: string; customer: CustomerRecord } | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const permissions = user?.kind === "staff" ? user.permissions : [];
  const allowed = (permission: string) => permissions.includes("*") || permissions.includes(permission);

  const history = usePagedCollection<CustomerDetail["tickets"][number]>(
    id,
    page,
    changePage,
    async (requestedPage, signal) => {
      const requestId = id;
      const response = await apiEnvelope<CustomerDetail>(
        `/agent/customers/${encodeURIComponent(requestId)}${queryString({ page: requestedPage, limit: COLLECTION_PAGE_SIZE })}`,
        { signal },
      );
      if (!signal.aborted && routeIdRef.current === requestId) {
        setCustomerState({ ownerId: requestId, customer: response.data.customer });
        if (formOwnerRef.current !== requestId || !formDirtyRef.current) {
          formOwnerRef.current = requestId;
          formDirtyRef.current = false;
          setForm(profileForm(response.data.customer));
        }
      }
      return {
        items: response.data.tickets,
        total: response.meta.total ?? response.data.customer.totalTickets ?? response.data.tickets.length,
      };
    },
  );
  const customer = customerState?.ownerId === id ? customerState.customer : null;
  const clampingPage = !history.loading && !history.refreshing && !history.error && page > history.totalPages;

  function customerSourceLabel(sourceSystem?: string) {
    if (sourceSystem === "email") return "Created from inbound email";
    if (sourceSystem === "dash") return "Next-Cart Dash customer";
    return sourceSystem ? `Source: ${sourceSystem}` : "Support customer";
  }

  useEffect(() => {
    if (previousIdRef.current === id) return;
    previousIdRef.current = id;
    formOwnerRef.current = "";
    formDirtyRef.current = false;
    setCustomerState(null);
    setForm(EMPTY_FORM);
    setBusy(false);
    if (page !== 1) changePage(1);
  }, [changePage, id, page]);

  useEffect(() => {
    if (clampingPage) changePage(history.totalPages);
  }, [changePage, clampingPage, history.totalPages]);

  function editForm(patch: Partial<typeof EMPTY_FORM>) {
    formDirtyRef.current = true;
    setForm((current) => ({ ...current, ...patch }));
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!customer) return;
    const actionId = id;
    const canViewSensitive = allowed("customer.view_sensitive");
    const input: { name: string; email?: string; phone?: string | null; locale: string; timezone: string } = {
      name: form.name,
      locale: form.locale,
      timezone: form.timezone,
      ...(canViewSensitive ? { email: form.email, phone: form.phone || null } : {}),
    };
    setBusy(true);
    try {
      await apiRequest(`/agent/customers/${encodeURIComponent(actionId)}`, { method: "PATCH", body: input });
      if (routeIdRef.current !== actionId) return;
      formDirtyRef.current = false;
      setCustomerState((current) => current?.ownerId === actionId ? { ownerId: actionId, customer: {
        ...current.customer,
        name: input.name,
        locale: input.locale,
        timezone: input.timezone,
        ...(input.email !== undefined ? { email: input.email } : {}),
        ...(Object.hasOwn(input, "phone") ? { phone: input.phone ?? undefined } : {}),
      } } : current);
      // Email is part of the ticket-history scope. Clear every accumulated
      // mobile page before loading page one for the newly saved identity.
      history.showNewest();
      if (routeIdRef.current === actionId) showToast("Customer profile updated.", "success");
    } catch (error) { showToast(error instanceof Error ? error.message : "Customer update failed.", "error"); }
    finally { if (routeIdRef.current === actionId) setBusy(false); }
  }

  async function setBlocked(blocked: boolean) {
    const actionId = id;
    setBusy(true);
    try {
      await apiRequest(`/agent/customers/${encodeURIComponent(actionId)}/block`, { method: "POST", body: { blocked, reason: blocked ? "Blocked by staff from customer profile" : undefined } });
      if (routeIdRef.current !== actionId) return;
      setCustomerState((current) => current?.ownerId === actionId ? { ownerId: actionId, customer: {
        ...current.customer, status: blocked ? "blocked" : "active", spamMarked: blocked ? false : current.customer.spamMarked,
      } } : current);
      history.showNewest();
      if (routeIdRef.current === actionId) showToast(blocked ? "Customer blocked. Existing sessions and ticket history remain available in read-only mode." : "Customer unblocked.", "success");
    } catch (error) { showToast(error instanceof Error ? error.message : "Customer status update failed.", "error"); }
    finally { if (routeIdRef.current === actionId) setBusy(false); }
  }

  async function setSpam(spam: boolean) {
    const actionId = id;
    setBusy(true);
    try {
      await apiRequest(`/agent/customers/${encodeURIComponent(actionId)}/spam`, { method: "POST", body: { spam, reason: spam ? "Marked as spam by staff from customer profile" : undefined } });
      if (routeIdRef.current !== actionId) return;
      setCustomerState((current) => current?.ownerId === actionId ? { ownerId: actionId, customer: { ...current.customer, spamMarked: spam } } : current);
      history.showNewest();
      if (routeIdRef.current === actionId) showToast(spam ? "Customer marked as spam. New tickets will be sent to Spam & quarantine." : "Spam mark removed.", "success");
    } catch (error) { showToast(error instanceof Error ? error.message : "Spam status update failed.", "error"); }
    finally { if (routeIdRef.current === actionId) setBusy(false); }
  }

  if (!customer && history.loading) return <PageLoader label="Loading customer profile..." />;
  if (!customer && history.error) return <PageError message={history.error} onRetry={() => void history.refresh()} />;
  if (!customer) return <PageError message="Customer not found." onRetry={() => void history.refresh()} />;
  const tickets = history.items;
  const blocked = customer.status === "blocked";
  const spamMarked = Boolean(customer.spamMarked);

  return <section className="agent-page page-stack customer-detail-page">
    <header className="agent-page-header customer-detail-header"><div><Link className="back-link" to="/agent/customers"><ArrowLeft aria-hidden="true" size={16} /> Customers</Link><h1>{customer.name}</h1><p>Customer identity, access controls, and complete support history.</p></div><div className="customer-state-badges"><span className={`customer-status customer-status--${customer.status}`}>{blocked ? "Blocked" : "Active"}</span>{spamMarked && <span className="customer-status customer-status--spam">Spam</span>}</div></header>
    {blocked && <div className="settings-callout settings-callout--warning"><Ban aria-hidden="true" /><p><strong>Customer is blocked</strong>They can still sign in and view old tickets, but cannot create tickets, reply, or upload attachments.</p></div>}
    {spamMarked && !blocked && <div className="settings-callout settings-callout--warning"><ShieldAlert aria-hidden="true" /><p><strong>Customer is marked as spam</strong>New tickets are created in Spam &amp; quarantine and remain visible to the customer with a warning.</p></div>}
    <div className="customer-detail-grid">
      <section className="card customer-profile-card"><div className="customer-profile-card__identity"><Avatar name={customer.name} size="large" /><div><h2>{customer.name}</h2><span>{customerSourceLabel(customer.sourceSystem)}</span></div></div>
        <div className="customer-profile-stats"><div><strong>{customer.openTickets ?? 0}</strong><span>Open tickets</span></div><div><strong>{customer.totalTickets ?? 0}</strong><span>Total tickets</span></div><div><strong>{formatRelative(customer.lastSeenAt)}</strong><span>Last seen</span></div></div>
        <dl className="customer-profile-meta"><div><dt><Mail size={15} /> Email</dt><dd>{customer.email ?? "Restricted"}</dd></div><div><dt><Phone size={15} /> Phone</dt><dd>{customer.phone || "Not provided"}</dd></div><div><dt>Created</dt><dd>{formatDate(customer.createdAt)}</dd></div></dl>
        {(allowed("customer.block") || allowed("customer.spam")) && <div className="customer-danger-actions">{allowed("customer.block") && <button className={`button ${blocked ? "button--secondary" : "button--danger"}`} disabled={busy} onClick={() => void setBlocked(!blocked)} type="button">{blocked ? <ShieldCheck size={16} /> : <Ban size={16} />}{blocked ? "Unblock customer" : "Block customer"}</button>}{allowed("customer.spam") && <button className={`button ${spamMarked ? "button--secondary" : "button--warning"}`} disabled={busy || blocked} title={blocked ? "Unblock the customer before marking them as spam" : undefined} onClick={() => void setSpam(!spamMarked)} type="button"><ShieldAlert size={16} />{spamMarked ? "Remove spam mark" : "Mark as spam"}</button>}</div>}
      </section>
      <form className="card customer-edit-form" onSubmit={save}><header><div><h2>Profile details</h2><p>Dash may refresh name and email on the next SSO login.</p></div></header><div className="form-grid"><label><span>Name</span><input value={form.name} disabled={!allowed("customer.update") || busy} onChange={(e) => editForm({ name: e.target.value })} /></label><label><span>Email</span><input type="email" value={form.email} disabled={!allowed("customer.update") || !allowed("customer.view_sensitive") || busy || !customer.email} onChange={(e) => editForm({ email: e.target.value })} /></label><label><span>Phone</span><input value={form.phone} disabled={!allowed("customer.update") || !allowed("customer.view_sensitive") || busy} onChange={(e) => editForm({ phone: e.target.value })} /></label><label><span>Locale</span><input value={form.locale} disabled={!allowed("customer.update") || busy} onChange={(e) => editForm({ locale: e.target.value })} /></label><label><span>Timezone</span><input value={form.timezone} disabled={!allowed("customer.update") || busy} onChange={(e) => editForm({ timezone: e.target.value })} /></label></div>{allowed("customer.update") && <footer><button className="button button--primary" disabled={busy} type="submit"><Save size={16} /> Save changes</button></footer>}</form>
    </div>
    <section ref={history.topRef} className="card customer-ticket-history" aria-busy={history.refreshing}><header><div><h2>Ticket history</h2><p>Tickets for this account and other customer records with the same email.{!history.loading && ` ${history.total} total.`}</p></div></header>{history.loading ? <PageLoader label="Loading ticket history..." /> : history.error ? <PageError message={history.error} onRetry={() => void history.refresh()} /> : clampingPage ? <PageLoader label="Opening the last available ticket page..." /> : tickets.length ? <div>{tickets.map((ticket) => <Link to={`/agent/tickets/${ticket.id}`} key={ticket.id}><span><strong>{ticket.number}</strong><small>{ticket.subject}</small></span>{ticket.quarantine_type && <span className={`quarantine-type-badge quarantine-type-badge--${ticket.quarantine_type}`}>{ticket.quarantine_type === "spam" ? "Spam" : "Security quarantine"}</span>}<span className={`customer-ticket-status customer-ticket-status--${ticket.status}`}>{ticket.status.replace("_", " ")}</span><time>{formatRelative(ticket.updatedAt)}</time></Link>)}</div> : <p className="empty-inline">This customer has no tickets.</p>}</section>
    {!history.error && !clampingPage && <CollectionPagination {...history} busy={history.refreshing} count={tickets.length} label="Customer ticket history pages" />}
  </section>;
}
